import {Profile} from "../../domain/profile.entity";
import {SocialLinks, UpdateProfileParams} from "../../domain/profile.types";
import {
  V2ProfileResponse,
  V2UpdateProfileRequest,
  V2UpdateProfileResponse,
} from "./profile.mapper.types";

/**
 * V2 profile mapper.
 * Converts between v2 API shapes (camelCase, nested user object) and domain entities.
 *
 * Pure TypeScript - no React imports.
 */
export const ProfileMapperV2 = {
  /**
   * Convert v2 API response to Profile entity.
   * Handles missing/null optional fields gracefully (Tolerant Reader).
   */
  toDomain(response: V2ProfileResponse): Profile {
    const {user} = response;

    const socialLinks: SocialLinks = {
      twitter: response.twitter ?? null,
      facebook: response.facebook ?? null,
      instagram: response.instagram ?? null,
      tiktok: response.tiktok ?? null,
      discord: response.discord ?? null,
      youtube: response.youtube ?? null,
      dribble: response.dribble ?? null,
      behance: response.behance ?? null,
      twitch: response.twitch ?? null,
      github: response.github ?? null,
      website: response.website ?? null,
      linkedin: response.linkedin ?? null,
    };

    return Profile.create({
      id: user.id,
      userId: response.userId,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      country: user.country ?? "",
      username: response.username ?? "",
      displayName: response.displayName ?? null,
      shortBio: response.shortBio ?? "",
      description: response.description ?? "",
      avatar: user.avatar ?? null,
      coverImage: user.coverImage ?? null,
      bufferTime: response.bufferTime ?? null,
      howDidYouFindUs: response.howDidYouFindUs ?? null,
      statusId: user.statusId,
      statusName: "Active",
      isSocialAuth: user.isSocialAuth ?? false,
      isKycVerified: false, // v2 does not provide this at profile level
      isWalletFrozen: false, // v2 does not provide this at profile level
      suspensionReason: null,
      walletFreezeReason: null,
      twoFactorMethod: user.twoFactorMethod ?? null,
      socialLinks,
      createdAt: response.createdAt ?? user.createdAt ?? "",
      updatedAt: response.updatedAt ?? user.updatedAt ?? "",
      lastDisplayNameChangeAt: null, // fetched separately via changelog
    });
  },

  /**
   * Convert v2 POST /profile response (flat, no nested user) to Profile entity.
   * Merges with the current profile to fill in fields not returned by the update endpoint
   * (id, email, avatar, coverImage, statusId, isSocialAuth, etc.).
   */
  toDomainFromUpdate(
    response: V2UpdateProfileResponse,
    currentProfile: Profile
  ): Profile {
    const socialLinks: SocialLinks = {
      twitter: response.twitter ?? null,
      facebook: response.facebook ?? null,
      instagram: response.instagram ?? null,
      tiktok: response.tiktok ?? null,
      discord: response.discord ?? null,
      youtube: response.youtube ?? null,
      dribble: response.dribble ?? null,
      behance: response.behance ?? null,
      twitch: response.twitch ?? null,
      github: response.github ?? null,
      website: response.website ?? null,
      linkedin: response.linkedin ?? null,
    };

    return Profile.create({
      // Fields from current profile (not in update response)
      id: currentProfile.id,
      email: currentProfile.email,
      firstName: currentProfile.firstName,
      lastName: currentProfile.lastName,
      country: currentProfile.country,
      avatar: currentProfile.avatar,
      coverImage: currentProfile.coverImage,
      statusId: currentProfile.statusId,
      statusName: currentProfile.statusName,
      isSocialAuth: currentProfile.isSocialAuth,
      isKycVerified: currentProfile.isKycVerified,
      isWalletFrozen: currentProfile.isWalletFrozen,
      suspensionReason: currentProfile.suspensionReason,
      walletFreezeReason: currentProfile.walletFreezeReason,
      lastDisplayNameChangeAt: currentProfile.lastDisplayNameChangeAt,
      twoFactorMethod: currentProfile.twoFactorMethod,
      // Fields from update response
      userId: response.userId,
      username: response.username ?? "",
      displayName: response.displayName ?? null,
      shortBio: response.shortBio ?? "",
      description: response.description ?? "",
      bufferTime: response.bufferTime ?? null,
      howDidYouFindUs: response.howDidYouFindUs ?? null,
      socialLinks,
      createdAt: response.createdAt ?? currentProfile.createdAt,
      updatedAt: response.updatedAt ?? currentProfile.updatedAt,
    });
  },

  /**
   * Convert domain UpdateProfileParams to v2 API request payload.
   */
  toUpdateRequest(params: UpdateProfileParams): V2UpdateProfileRequest {
    return {
      firstName: params.firstName,
      lastName: params.lastName,
      displayName: params.displayName,
      shortBio: params.shortBio,
      description: params.description,
      country: params.country,
      username: params.username,
      howDidYouFindUs: params.howDidYouFindUs,
      facebook: params.socialLinks.facebook,
      twitter: params.socialLinks.twitter,
      instagram: params.socialLinks.instagram,
      discord: params.socialLinks.discord,
      youtube: params.socialLinks.youtube,
      dribble: params.socialLinks.dribble,
      twitch: params.socialLinks.twitch,
      github: params.socialLinks.github,
      website: params.socialLinks.website,
      tiktok: params.socialLinks.tiktok,
      linkedin: params.socialLinks.linkedin,
      behance: params.socialLinks.behance,
    };
  },
};
